import { AUTH } from '@/constants/auth'
import GeneralError from '@/features/errors/general-error'
import NotFoundError from '@/features/errors/not-found-error'
import { AuthStore } from '@/stores/auth.store'
import { QueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { FirebaseApp } from 'firebase/app'
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import React from 'react'

const REDIRECT_WHEN_AUTHENTICATED_ROUTE = '/'
const REDIRECT_WHEN_NOT_AUTHENTICATED_ROUTE = '/sign-in'

const redirectToAuthenticatedRoute = () =>
  redirect({ to: REDIRECT_WHEN_AUTHENTICATED_ROUTE })
const redirectToNotAuthenticatedRoute = () =>
  redirect({ to: REDIRECT_WHEN_NOT_AUTHENTICATED_ROUTE })

type PublicRoute = {
  path: string
  whenAuthenticated: 'redirect' | 'stay'
}

const publicRoutes: PublicRoute[] = [
  { path: '/sign-in', whenAuthenticated: 'redirect' },
  { path: '/sign-up', whenAuthenticated: 'redirect' },
  { path: '/forgot-password', whenAuthenticated: 'redirect' },
  { path: '/reset-password', whenAuthenticated: 'redirect' },
  { path: '/verify-email', whenAuthenticated: 'redirect' },
] as const

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
  firebaseApp: FirebaseApp
  store: {
    auth: AuthStore
  }
}>()({
  beforeLoad(ctx) {
    const path = ctx.location.pathname
    const publicRoute = publicRoutes.find((route) => path === route.path)
    const token = Cookies.get(AUTH.REFRESH_TOKEN)

    if (!token && publicRoute) return

    if (token && publicRoute?.whenAuthenticated === 'redirect') {
      throw redirectToAuthenticatedRoute()
    }

    if (!token && !publicRoute) {
      throw redirectToNotAuthenticatedRoute()
    }

    if (token && !publicRoute) {
      const decoded = jwtDecode<{ exp: number }>(token)
      const isExpired = decoded.exp < Date.now() / 1000

      if (isExpired) {
        Cookies.remove(AUTH.ACCESS_TOKEN)
        Cookies.remove(AUTH.REFRESH_TOKEN)
        throw redirectToNotAuthenticatedRoute()
      }
    }
  },
  component: () => {
    return (
      <React.Fragment>
        <HeadContent />
        <Outlet />
        {import.meta.env.DEV && (
          <ReactQueryDevtools buttonPosition='bottom-right' />
        )}
      </React.Fragment>
    )
  },
  notFoundComponent: NotFoundError,
  errorComponent: GeneralError,
})
